/**
 * Data Encryption/Decryption Utility
 * Handles AES-256-CBC encryption/decryption for secure communication with backend
 * Matches Laravel's encryption format exactly
 */

import CryptoJS from 'crypto-js';

/**
 * Get encryption key from environment
 * Must match Laravel's APP_KEY
 */
const getEncryptionKey = () => {
  let key = process.env.REACT_APP_ENCRYPTION_KEY || process.env.REACT_APP_APP_KEY;
  
  if (!key) {
    console.error('❌ ENCRYPTION KEY NOT SET! Add REACT_APP_ENCRYPTION_KEY to .env');
    console.error('⚠️  Encryption is DISABLED until key is configured');
    return null;
  }
  
  // Handle Laravel's base64: prefix
  if (key.startsWith('base64:')) {
    return CryptoJS.enc.Base64.parse(key.substring(7));
  }
  
  return CryptoJS.enc.Utf8.parse(key);
};

/**
 * Encrypt data using AES-256-CBC (matches Laravel backend)
 * @param {any} data - Data to encrypt (will be JSON stringified)
 * @returns {string} - Base64 encoded encrypted string (IV + ciphertext)
 */
export const encryptData = (data) => {
  try {
    // Get encryption key (returns null if not configured)
    const key = getEncryptionKey();
    
    // If no key, fall back to base64 encoding
    if (!key) {
      console.warn('⚠️  No encryption key - falling back to base64 encoding');
      return encodeData(data);
    }
    
    const jsonString = JSON.stringify(data);
    
    // Generate random IV (16 bytes for AES)
    const iv = CryptoJS.lib.WordArray.random(16);
    
    // Encrypt using AES-256-CBC
    const encrypted = CryptoJS.AES.encrypt(jsonString, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    
    // Combine IV and encrypted data (same format as Laravel)
    const combined = iv.concat(encrypted.ciphertext);
    
    // Return base64 encoded string
    return CryptoJS.enc.Base64.stringify(combined);
  } catch (error) {
    console.error('❌ Error encrypting data:', error);
    console.warn('⚠️  Falling back to base64 encoding');
    return encodeData(data);
  }
};

/**
 * Decrypt data using AES-256-CBC (matches Laravel backend)
 * @param {string} encryptedData - Base64 encoded encrypted string
 * @returns {any} - Decrypted and parsed data
 */
export const decryptData = (encryptedData) => {
  try {
    // Get encryption key (returns null if not configured)
    const key = getEncryptionKey();
    
    // If no key, try base64 decoding
    if (!key) {
      console.warn('⚠️  No encryption key - trying base64 decode');
      return decodeData(encryptedData);
    }
    
    // Parse base64 encoded data
    const combined = CryptoJS.enc.Base64.parse(encryptedData);
    
    // Extract IV (first 16 bytes / 4 words)
    const iv = CryptoJS.lib.WordArray.create(combined.words.slice(0, 4));
    
    // Extract ciphertext (remaining bytes)
    const ciphertext = CryptoJS.lib.WordArray.create(combined.words.slice(4));
    
    // Decrypt using AES-256-CBC
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertext },
      key,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    );
    
    // Convert to UTF-8 string
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedString) {
      throw new Error('Decryption resulted in empty string - wrong key?');
    }
    
    // Parse JSON
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error('❌ Error decrypting data:', error);
    console.error('🔍 Decryption details:', {
      errorMessage: error.message,
      errorStack: error.stack,
      encryptedDataLength: encryptedData?.length,
      encryptedDataSample: encryptedData?.substring(0, 50) + '...',
      keyLength: getEncryptionKey()?.sigBytes || 0,
      keySet: !!getEncryptionKey()
    });
    
    // Don't fall back - throw the error so we know decryption failed
    throw new Error('Decryption failed: ' + error.message + ' - Check encryption keys match!');
  }
};

/**
 * Legacy Base64 encoding (fallback for non-sensitive data)
 */
export const encodeData = (data) => {
  try {
    const jsonString = JSON.stringify(data);
    
    const base64 = btoa(
      encodeURIComponent(jsonString).replace(
        /%([0-9A-F]{2})/g,
        (match, p1) => String.fromCharCode(`0x${p1}`)
      )
    );
    
    return base64;
  } catch (error) {
    console.error('Error encoding data:', error);
    throw new Error('Failed to encode data');
  }
};

/**
 * Legacy Base64 decoding (fallback for non-sensitive data)
 */
export const decodeData = (encodedData) => {
  try {
    const jsonString = decodeURIComponent(
      atob(encodedData)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Error decoding data:', error);
    throw new Error('Failed to decode data');
  }
};

